import React, { createContext, useRef, useState, useEffect, useContext, useCallback } from 'react';
import '@pixi/layout'; // eslint-disable-line
import * as PIXI from 'pixi.js';
import gsap from 'gsap';
import { PixiPlugin } from 'gsap/PixiPlugin';
import { CustomWiggle } from 'gsap/CustomWiggle';
import { CustomEase } from 'gsap/CustomEase';

const PixiAppContext = createContext(null);

export const RESOLUTION = 1;
export const APP_WIDTH = 1080 * RESOLUTION;
export const APP_HEIGHT = 2330 * RESOLUTION;
export const MAX_WIDTH = 1200 * RESOLUTION;

export const usePixiApp = () => {
    const context = useContext(PixiAppContext);
    if (!context) {
        throw new Error('usePixiApp must be used within a PixiAppProvider');
    }
    return context;
};

function tickGSAPWhileHidden(value) {
    if (value === false) {
        document.removeEventListener("visibilitychange", tickGSAPWhileHidden.fn);
        return clearInterval(tickGSAPWhileHidden.id);
    }
    const onChange = () => {
        clearInterval(tickGSAPWhileHidden.id);
        if (document.hidden) {
            gsap.ticker.lagSmoothing(0); // keep the time moving forward (don't adjust for lag)
            tickGSAPWhileHidden.id = setInterval(gsap.ticker.tick, 500);
        } else {
            gsap.ticker.lagSmoothing(500, 33); // restore lag smoothing
        }
    };
    document.addEventListener("visibilitychange", onChange);
    tickGSAPWhileHidden.fn = onChange;
    onChange(); // in case the document is currently hidden.
}

export const PixiAppProvider = ({ children, width = APP_WIDTH, height = APP_HEIGHT, background = '#0078e5' }) => {
    const [app, setApp] = useState(null);
    const appRef = useRef(null);

    useEffect(() => {
        let pixiApp;
        let destroyed = false;

        (async () => {
            await Promise.all([...(await document.fonts.ready)].map(font => font.load()));

            pixiApp = new PIXI.Application();
            await pixiApp.init({
                width,
                height,
                background,
            });

            window.globalThis.__PIXI_APP__ = pixiApp;

            const { canvas } = pixiApp;
            canvas.style.maxWidth = '100%';
            canvas.style.maxHeight = '100%';
            canvas.style.position = 'relative';
            canvas.style.top = '50%';
            canvas.style.left = '50%';
            canvas.style.transform = 'translate(-50%, -50%)';

            canvas.addEventListener('touchstart', (e) => e.preventDefault(), { passive: false });

            PixiPlugin.registerPIXI(PIXI);
            gsap.registerPlugin(PixiPlugin, CustomEase, CustomWiggle);
            tickGSAPWhileHidden(true);

            if (!destroyed) {
                setApp(pixiApp);
                appRef.current = pixiApp;
            }
        })();

        return () => {
            destroyed = true;
            if (appRef.current && !appRef.current.renderer._destroyed) {
                appRef.current.destroy(true, { children: true, texture: true, baseTexture: true });
            }
            setApp(null);
        };
        // Only run this once
        // eslint-disable-next-line
    }, [width, height, background]);

    const onResize = () => {
        if (appRef.current) {
            let newWidth = APP_HEIGHT * (window.innerWidth / window.innerHeight);
            if (newWidth < APP_WIDTH) {
                newWidth = APP_WIDTH;
            }

            if (newWidth > MAX_WIDTH) {
                newWidth = MAX_WIDTH;
            }

            appRef.current.renderer.resize(newWidth, APP_HEIGHT);
            window.dispatchEvent(new Event('appResize'));
        }
    };

    const setAutoResize = useCallback((isAutoResize) => {
        if (isAutoResize) {
            window.addEventListener('resize', onResize);
            onResize();
        } else {
            window.removeEventListener('resize', onResize);
            appRef.current?.renderer.resize(APP_WIDTH, APP_HEIGHT);
        }
    }, []);

    // useEffect(() => {
    //     if (isAutoResize) {
    //         window.addEventListener('resize', onResize);
    //         onResize();
    //     } else {
    //         window.removeEventListener('resize', onResize);
    //         appRef.current?.renderer.resize(APP_WIDTH, APP_HEIGHT);
    //     }

    //     return () => {
    //         window.removeEventListener('resize', onResize);
    //     };
    // }, [isAutoResize]);

    return (
        <PixiAppContext.Provider value={{ app, setAutoResize }}>
            {children}
        </PixiAppContext.Provider>
    );
};

export default PixiAppContext;
